import { toOpaqueColor } from '../utils/formatters';

// Compact-window equivalent of `getResponsiveStyles` (Message_Style.js).
// Returns objects under the EXACT SAME keys that ChatHeader / ChatInput /
// MessagesContainer / MessageBubble already expect, just sized for a small
// floating window instead of a full page — this is what lets those
// feature-complete components be reused completely unmodified in the Mini
// Chat Dock.
//
// Uses the app's own existing messenger tokens (messengerColors.js) rather
// than one-off hex values: `popoverBg` is the system's floating-surface
// color (dropdowns/popovers use it app-wide) for the header/input chrome,
// and `chatBg` is the same background the full Messenger page's own message
// area already uses. Both are only partially opaque by design (the full
// page's glass effect) — `toOpaqueColor` strips that alpha so this small
// floating window, sitting over arbitrary page content, is always solid.
export const getMiniChatStyles = (c, isRTL) => {
  const solidBg = toOpaqueColor(c.popoverBg);
  const solidPanelBg = toOpaqueColor(c.chatBg);
  return {
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontWeight: '700',
      fontSize: '13px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      border: `2px solid ${solidBg}`,
      transition: 'all 0.2s ease',
    },
    onlineIndicator: {
      position: 'absolute',
      bottom: '0px',
      right: isRTL ? 'auto' : '0px',
      left: isRTL ? '0px' : 'auto',
      width: '11px',
      height: '11px',
      background: c.online,
      border: `2px solid ${solidBg}`,
      borderRadius: '50%',
      boxShadow: '0 1px 4px rgba(34, 197, 94, 0.4)',
    },
    backButton: {
      padding: '6px',
      background: 'transparent',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'none', // no mobile-style back nav inside a floating window
      alignItems: 'center',
      justifyContent: 'center',
      color: c.text,
    },
    chatHeader: {
      background: solidBg,
      borderBottom: `1px solid ${c.border}`,
      padding: '10px 12px',
      flexShrink: 0,
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
    },
    messagesContainer: {
      position: 'relative',
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: '8px',
      backgroundColor: solidPanelBg,
      minHeight: 0,
    },
    // Smaller + tighter than the full page's bubble — two windows open
    // side-by-side need every pixel of horizontal room they can get.
    messageBubble: {
      padding: '6px 10px',
      borderRadius: '13px',
      fontSize: '12.5px',
      width: 'fit-content',
      maxWidth: '100%',
      wordBreak: 'break-word',
      position: 'relative',
      lineHeight: '1.4',
    },
    myMessage: {
      background: c.myMessageGradient || '#2e7cba',
      color: '#ffffff',
      borderBottomRightRadius: isRTL ? '13px' : '4px',
      borderBottomLeftRadius: isRTL ? '4px' : '13px',
      marginLeft: isRTL ? '0' : 'auto',
      marginRight: isRTL ? 'auto' : '0',
      boxShadow: c.myBubbleShadow || '0 1px 6px rgba(46, 124, 186, 0.22)',
      border: 'none',
    },
    theirMessage: {
      background: c.theirMessageBg,
      color: c.text,
      borderBottomRightRadius: isRTL ? '4px' : '13px',
      borderBottomLeftRadius: isRTL ? '13px' : '4px',
      marginRight: isRTL ? '0' : 'auto',
      marginLeft: isRTL ? 'auto' : '0',
      border: `1px solid ${c.border}`,
    },
    inputArea: {
      background: solidBg,
      borderTop: `1px solid ${c.border}`,
      padding: '8px',
      flexShrink: 0,
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',
    },
    inputContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      background: c.inputBg,
      borderRadius: '20px',
      padding: '4px 8px',
      border: `1.5px solid ${c.inputBorder}`,
    },
    iconButton: {
      padding: '7px',
      color: '#2e7cba',
      background: 'transparent',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    messageInput: {
      flex: 1,
      padding: '8px 10px',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      fontSize: '13.5px',
      color: c.text,
      minHeight: '34px',
      maxHeight: '90px',
      resize: 'none',
      fontFamily: 'inherit',
      lineHeight: '1.4',
    },
    sendButton: {
      padding: '9px',
      background: 'linear-gradient(135deg, #2e7cba, #3b82f6)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    },
    sendButtonDisabled: {
      background: c.textSecondary,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  };
};

export default getMiniChatStyles;
